import React, { useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Car, PlusCircle, Wand2, Send, Sparkles,
  Plug, Settings, LogOut, Menu, Globe, Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import TourProvider from "@/components/tour/TourProvider";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app", key: "nav.dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/fahrzeuge", key: "nav.vehicles", icon: Car },
  { to: "/app/aufnehmen", key: "nav.capture", icon: PlusCircle, cta: true },
  { to: "/app/fotobooth", key: "nav.fotobooth", icon: Wand2 },
  { to: "/app/veroeffentlichen", key: "nav.publishing", icon: Send },
  { to: "/app/content", key: "nav.content", icon: Sparkles },
  { to: "/app/integrationen", key: "nav.integrations", icon: Plug },
  { to: "/app/einstellungen", key: "nav.settings", icon: Settings },
];

const Logo = () => (
  <Link to="/app" className="flex items-center gap-2 px-2" data-testid="appshell-logo-link">
    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
      <Car className="h-4 w-4 text-primary" />
    </div>
    <div>
      <div className="font-display text-lg font-bold leading-none">CarOS</div>
      <div className="text-[10px] text-muted-foreground leading-tight">Vehicle-to-Market in Minuten</div>
    </div>
  </Link>
);

const NavItems = ({ onNavigate }) => {
  const { t } = useLang();
  return (
    <nav className="flex flex-col gap-1 mt-4">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.key.split(".")[1]}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
              item.cta
                ? "bg-primary text-primary-foreground font-semibold hover:brightness-110 my-1"
                : isActive
                ? "bg-accent text-foreground border-l-2 border-primary"
                : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
            )
          }
        >
          <item.icon className="h-4 w-4 shrink-0" />
          {t(item.key)}
        </NavLink>
      ))}
    </nav>
  );
};

const UserFooter = () => {
  const { user, tenant, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  if (!user) return null;
  return (
    <div className="mt-auto border-t border-border pt-3 px-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{user.name}</div>
          <div className="text-xs text-muted-foreground truncate">{tenant?.name}</div>
        </div>
        <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button" className="gap-1.5 text-xs shrink-0" aria-label={t("common.logout")}>
          <LogOut className="h-3.5 w-3.5" /> {t("common.logout")}
        </Button>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2 text-xs"
        onClick={() => setLang(lang === "de" ? "en" : "de")}
        data-testid="language-toggle"
      >
        <Globe className="h-3.5 w-3.5" />
        {lang === "de" ? "Deutsch → English" : "English → Deutsch"}
      </Button>
      <div className="flex items-center justify-between px-1 pt-1.5 border-t border-border/60">
        <a
          href="https://cloudservice.hr"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          data-testid="appshell-developer-link"
        >
          Entwickelt von cloudservice.hr
        </a>
        <a
          href="https://www.instagram.com/cloudservice.hr"
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          aria-label="Instagram: cloudservice.hr"
          data-testid="appshell-instagram-link"
        >
          <Instagram className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};

export const AppShell = ({ children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();

  return (
    <TourProvider>
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col border-r border-border bg-card/60 p-4 z-40">
        <Logo />
        <NavItems />
        <UserFooter />
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background/85 backdrop-blur px-4 py-3">
        <Logo />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => navigate("/app/aufnehmen")}
            data-testid="mobile-capture-button"
            className="gap-1.5"
          >
            <PlusCircle className="h-4 w-4" /> {t("nav.capture")}
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] flex flex-col p-4">
              <Logo />
              <NavItems onNavigate={() => setOpen(false)} />
              <UserFooter />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="lg:pl-[260px] overflow-x-hidden">
        <div key={location.pathname} className="px-4 py-5 lg:px-8 lg:py-7 max-w-[1500px] min-w-0">{children}</div>
      </main>
    </div>
    </TourProvider>
  );
};

export default AppShell;
